import {
  Navigate,
  Link,
  Outlet,
  createRootRoute,
  createRoute,
  createRouter
} from "@tanstack/react-router";
import { useRouterState } from "@tanstack/react-router";
import { hasPermission, type Permission, type UserRole } from "@htzap/contracts";
import { activeSettingsSectionFromHash, canAccessSettingsSection } from "./lib/settings-navigation";
import { lazy, Suspense, useEffect, useRef } from "react";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "./lib/auth";
import { LoginPage } from "./pages/LoginPage";
import { ChangePasswordPage } from "./pages/ChangePasswordPage";
import { Shell } from "./components/Shell";

const InboxPage = lazy(() => import("./pages/InboxPage").then(module => ({ default: module.InboxPage })));
const ContactsPage = lazy(() => import("./pages/ContactsPage").then(module => ({ default: module.ContactsPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(module => ({ default: module.SettingsPage })));
const TeamPage = lazy(() => import("./pages/TeamPage").then(module => ({ default: module.TeamPage })));
const TeamChatPage = lazy(() => import("./pages/TeamChatPage").then(module => ({ default: module.TeamChatPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then(module => ({ default: module.DashboardPage })));
const AgendaPage = lazy(() => import("./pages/AgendaPage").then(module => ({ default: module.AgendaPage })));
const StockPage = lazy(() => import("./pages/StockPage").then(module => ({ default: module.StockPage })));

const FlowsPage = lazy(() =>
  import("./pages/FlowsPage").then((module) => ({ default: module.FlowsPage }))
);

const ProcessesPage = lazy(() => import("./pages/ProcessesPage").then(module => ({ default: module.ProcessesPage })));

function RootComponent() {
  return <Outlet />;
}

function optionalSearchId(value: unknown) {
  return typeof value === "string" && value.length <= 80 ? value : undefined;
}

function Protected({
  children,
  roles,
  permission,
  allowPasswordChange = false
}: {
  children: React.ReactNode;
  roles?: UserRole[];
  permission?: Permission;
  allowPasswordChange?: boolean;
}) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="app-loading">
        <span className="brand-mark">
          <span>H</span>
        </span>
        <p>Preparando seu workspace...</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;
  if (user.mustChangePassword && !allowPasswordChange) {
    return <Navigate to="/change-password" />;
  }
  if (!user.mustChangePassword && allowPasswordChange) {
    return <AdminAccessRequired />;
  }
  if (permission && !hasPermission(user, permission)) return <AdminAccessRequired />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/inbox" />;
  }
  return <Suspense fallback={<div className="app-loading" role="status"><p>Carregando página...</p></div>}>{children}</Suspense>;
}

function AdminAccessRequired({ backSection }: { backSection?: string }) {
  return (
    <section className="standard-page">
      <header className="access-required-banner" role="alert">
        <span className="access-required-icon" aria-hidden="true"><ShieldCheck size={24} /></span>
        <div className="access-required-copy">
          <span className="eyebrow">Permissão administrativa</span>
          <h1>Acesso restrito</h1>
          <p>É necessário acesso de Técnico Admin para utilizar esta funcionalidade.</p>
          {backSection ? <Link className="access-required-back" to="/settings" hash={backSection}><ArrowLeft size={16} aria-hidden="true" />Voltar às configurações</Link> : null}
        </div>
      </header>
    </section>
  );
}

const rootRoute = createRootRoute({ component: RootComponent });

function AuthenticatedLayout() {
  return (
    <Protected>
      <Shell>
        <Suspense fallback={<div className="app-loading" role="status"><p>Carregando página...</p></div>}>
          <Outlet />
        </Suspense>
      </Shell>
    </Protected>
  );
}

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_authenticated",
  component: AuthenticatedLayout
});
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => <Navigate to="/inbox" />
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage
});
const changePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/change-password",
  component: () => (
    <Protected allowPasswordChange>
      <ChangePasswordPage />
    </Protected>
  )
});
function InboxRouteComponent() {
  const { ticket, newContact } = inboxRoute.useSearch();
  return (
    <InboxPage initialTicketId={ticket} initialNewContactId={newContact} />
  );
}

const inboxRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/inbox",
  validateSearch: (
    search: Record<string, unknown>
  ): { ticket?: string; newContact?: string } => {
    const ticket = optionalSearchId(search.ticket);
    const newContact = optionalSearchId(search.newContact);
    return {
      ...(ticket ? { ticket } : {}),
      ...(newContact ? { newContact } : {})
    };
  },
  component: InboxRouteComponent
});

function ContactsRouteComponent() {
  const { contact } = contactsRoute.useSearch();
  return (
    <ContactsPage initialContactId={contact} />
  );
}

const contactsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/contacts",
  validateSearch: (search: Record<string, unknown>): { contact?: string } => {
    const contact = optionalSearchId(search.contact);
    return contact ? { contact } : {};
  },
  component: ContactsRouteComponent
});
function AgendaRouteComponent() {
  const { date, view } = agendaRoute.useSearch();
  return <AgendaPage initialDate={date} initialView={view} />;
}

const agendaRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/agenda",
  validateSearch: (search: Record<string, unknown>): { date?: string; view?: "overdue" } => {
    const date = typeof search.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.date)
      ? search.date
      : undefined;
    return { ...(date ? { date } : {}), ...(search.view === "overdue" ? { view: "overdue" as const } : {}) };
  },
  component: AgendaRouteComponent
});
const stockRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/stock",
  component: () => <StockPage />
});
const teamRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/team",
  component: () => (
    <Protected permission="team.view">
      <TeamPage />
    </Protected>
  )
});
const dashboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/dashboard",
  component: () => <Protected permission="dashboard.view"><DashboardPage /></Protected>
});
function FlowsRouteComponent() {
  const { flow } = flowsRoute.useSearch();
  return (
    <Protected permission="flows.edit">
      <Suspense fallback={<div className="app-loading"><p>Preparando o Criador de Fluxos...</p></div>}>
        <FlowsPage initialFlowId={flow} />
      </Suspense>
    </Protected>
  );
}

const flowsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/flows",
  validateSearch: (search: Record<string, unknown>): { flow?: string } => {
    const flow = optionalSearchId(search.flow);
    return flow ? { flow } : {};
  },
  component: FlowsRouteComponent
});
const teamChatRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/team-chat",
  component: () => (
    <Protected>
      <TeamChatPage />
    </Protected>
  )
});
const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/settings",
  component: SettingsRouteComponent
});

function SettingsRouteComponent() {
  const { user } = useAuth();
  const hash = useRouterState({ select: (state) => state.location.hash });
  const lastAllowedSection = useRef("companies");
  useEffect(() => {
    const section = activeSettingsSectionFromHash(hash);
    if (user && canAccessSettingsSection(user, section)) lastAllowedSection.current = section;
  }, [hash, user]);
  if (user && hash && !canAccessSettingsSection(user, activeSettingsSectionFromHash(hash))) {
    return <AdminAccessRequired backSection={lastAllowedSection.current} />;
  }
  return <Protected permission="team.view"><SettingsPage /></Protected>;
}


const processesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/processes",
  validateSearch: (search: Record<string, unknown>): { process?: string; step?: string; tab?: "processes" | "alerts" | "templates" | "teams" } => ({
    process: optionalSearchId(search.process), step: optionalSearchId(search.step),
    tab: search.tab === "alerts" || search.tab === "templates" || search.tab === "teams" ? search.tab : "processes"
  }),
  component: ProcessesRouteComponent
});
function ProcessesRouteComponent() {
  const search = processesRoute.useSearch();
  return <Protected permission="processes.use"><ProcessesPage initialProcessId={search.process} initialStepId={search.step} initialTab={search.tab} /></Protected>;
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  changePasswordRoute,
  authLayoutRoute.addChildren([
    inboxRoute,
    contactsRoute,
    agendaRoute,
    stockRoute,
    dashboardRoute,
    flowsRoute,
    teamChatRoute,
    teamRoute,
    processesRoute,
    settingsRoute
  ])
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
